import { format } from "date-fns";
import { useEffect, useState } from "react";
import { FaArrowLeft } from "react-icons/fa6";
import { RxCross2 } from "react-icons/rx";
import { useNavigate, useParams } from "react-router-dom";
import { LIVE_URL } from "../../../config/config";
import useAuthToken from "../../../hooks/useAuthToken";
import CustomModal from "../customModel/CustomModel";
import Spinner from "../spinner/Spinner";
import styles from "./css/UserInfo.module.scss";

const UserInfo = () => {
  const { id } = useParams();
  const { token } = useAuthToken();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${LIVE_URL}/user/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        setUserData(data.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (!loading && token) {
      fetchUserData();
      setLoading(false);
    }
  }, [token, loading, id]);

  // Effect to manage popup message
  useEffect(() => {
    let timer;
    if (popupMessage) {
      const popupElement = document.getElementById("popup-message");
      if (popupElement) {
        popupElement.style.opacity = "1";
      }
      timer = setTimeout(() => {
        if (popupElement) {
          popupElement.style.opacity = "0";
        }
        setTimeout(() => {
          setPopupMessage("");
        }, 500);
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [popupMessage]);

  // Render a message if no user data is available
  if (!userData) {
    return <Spinner />;
  }

  const profileImage =
    userData.user_images && userData.user_images.length > 0
      ? userData.user_images[0].image
      : null;

  // Delete image
  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(`${LIVE_URL}/admin/delete-user-image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userData.id,
          image_id: selectedImageId,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setUserData((prev) => ({
          ...prev,
          user_images: prev.user_images.filter(
            (img) => img.id !== selectedImageId,
          ),
        }));

        setModalOpen(false);
        setSelectedImageId(null);
      }
    } catch (error) {
      console.error("Delete Image Error:", error);
    }
  };

  return (
    <>
      {/* Popup message container */}
      <div
        id="popup-message"
        className={`${styles.update_message} ${
          popupMessage.includes("success") ? styles.success : styles.error
        }`}
      >
        {popupMessage && <p>{popupMessage}</p>}
      </div>
      <div className={`${styles.user_info}`}>
        <header>
          <h2>Profile Overview</h2>
        </header>
        <section className={`${styles.section}`}>
          <div className={`${styles.profile_image}`}>
            <div className={`${styles.back_button}`}>
              {/* <button onclick={() => navigate("home/user")}> */}
              <button onClick={() => navigate("/home/user")}>
                <FaArrowLeft />
                Go to user list
              </button>
            </div>

            <div className={`${styles.user_profile}`}>
              <div className={`${styles.profile_header}`}>
                <p>My Profile</p>
                <span>
                  {userData?.user_subscription.plan_name || "No Membership"}
                </span>
              </div>
              <div className={`${styles.user_image}`}>
                {profileImage ? (
                  <img src={profileImage} alt="User" />
                ) : (
                  <div className={styles.no_image_placeholder}>
                    No Image Available
                  </div>
                )}

                <h5>
                  {userData.firstname} {userData.lastname}
                </h5>
              </div>
            </div>
            <div className={`${styles.payment_info}`}>
              <p>Other Picture</p>
              <div className={styles.image_wrapper}>
                {userData.user_images && userData.user_images.length > 0 ? (
                  userData.user_images.slice(0, 4).map((image, index) => (
                    <div className={styles.image_box} key={index}>
                      <img src={image.image} alt={`UserImage${index + 1}`} />

                      <div
                        className={styles.delete_icon}
                        onClick={() => {
                          setSelectedImageId(image.id);
                          setModalOpen(true);
                        }}
                      >
                        <RxCross2 />
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No image available</p>
                )}
              </div>
            </div>
            <div className={`${styles.plan_info}`}>
              <div className={`${styles.plan_button}`}>
                <p>
                  Plan Name :{" "}
                  {userData?.user_subscription.plan_name || "No Membership"}
                </p>
                <p>Plan Amount : {userData?.user_subscription.price || "0"}</p>
              </div>
              <div className={`${styles.plan_date}`}>
                <div>
                  <p>Plan Start Date:</p>
                  <h6>
                    {userData?.user_subscription?.created_at
                      ? format(
                          new Date(userData.user_subscription.created_at),
                          "yyyy-MM-dd",
                        )
                      : "N/A"}
                  </h6>
                </div>
                <div>
                  <p>Plan End Date:</p>
                  <h6>
                    {userData?.user_subscription?.expired_at
                      ? format(
                          new Date(userData.user_subscription.expired_at),
                          "yyyy-MM-dd",
                        )
                      : "N/A"}
                  </h6>
                </div>
              </div>
            </div>
          </div>
          <div className={`${styles.other_info}`}>
            <h1>Other Information</h1>
            <div className={`${styles.header}`}>
              <div className={styles.info_item}>
                <h4>Profile Bio :</h4>
                <p>{userData.bio || "N/A"}</p>
              </div>
              <div className={styles.info_item}>
                <h4>Birth Date :</h4>
                <p>{new Date(userData.dob).toLocaleDateString() || "N/A"}</p>
              </div>
              <div className={styles.info_item}>
                <h4>Gender :</h4>
                <p>{userData.gender || "N/A"}</p>
              </div>
              <div className={styles.info_item}>
                <h4>Height :</h4>
                <p>{userData.height || "N/A"}</p>
              </div>
              <div className={styles.info_item}>
                <h4>Search Preference :</h4>
                <p>{userData.interest_in || "N/A"}</p>
              </div>
              <div className={styles.info_item}>
                <h4>Relation Goal :</h4>
                <p>
                  {userData.relation_goal_data
                    .map((goal) => goal.title)
                    .join(", ") || "N/A"}
                </p>
              </div>
              <div className={styles.info_item}>
                <h4>Religion :</h4>
                <p>
                  {userData.religion_data
                    .map((religion) => religion.title)
                    .join(", ") || "N/A"}
                </p>
                {/* <p>{userData.religion}</p> */}
              </div>
              <div className={styles.info_item}>
                <h4>Zodiac sign :</h4>
                <p>{userData.zodiac_sign || "N/A"}</p>
              </div>
              <div className={styles.info_item}>
                <h4>Interests :</h4>
                <p>
                  {userData.interests_data
                    .map((interest) => interest.title)
                    .join(", ") || "N/A"}
                </p>
              </div>
              <div className={styles.info_item}>
                <h4>Languages :</h4>
                <p>
                  {userData.languages_data
                    .map((language) => language.title)
                    .join(", ") || "N/A"}
                </p>
              </div>
              <div className={styles.info_item}>
                <h4>Age Range :</h4>
                <p>{userData.age_range || "N/A"}</p>
              </div>
            </div>
          </div>
        </section>

        <main className={`${styles.main}`}>
          <div className={`${styles.user_interest}`}>
            <p>Interest</p>
            <div className={styles.userContainer}>
              {userData.interests_data && userData.interests_data.length > 0 ? (
                userData.interests_data.slice(0, 5).map((interest, index) => (
                  <div key={index} className={styles.interestItem}>
                    {interest.image && interest.image.trim() !== "" ? (
                      <img src={interest.image} alt={`interest${index + 1}`} />
                    ) : (
                      <p className={styles.noImageText}>No image available</p>
                    )}
                    <p>{interest.title}</p>
                  </div>
                ))
              ) : (
                <p style={{ color: "gray" }}>N/A</p>
              )}
            </div>
          </div>

          <div className={`${styles.user_language}`}>
            <p>Language Known</p>
            <div className={styles.languageContainer}>
              {userData.languages_data && userData.languages_data.length > 0 ? (
                userData.languages_data.slice(0, 5).map((language, index) => (
                  <div key={index} className={styles.languageItem}>
                    {language.image && language.image.trim() !== "" ? (
                      <img src={language.image} alt={`language${index + 1}`} />
                    ) : (
                      <p className={styles.noImageText}>No image available</p>
                    )}
                    <p>{language.title}</p>
                  </div>
                ))
              ) : (
                <p style={{ color: "gray" }}>N/A</p>
              )}
            </div>
          </div>
        </main>
      </div>

      <CustomModal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default UserInfo;
